import { createApp } from "vue";
import { createPinia } from "pinia";
import { Tooltip } from "bootstrap";
import App from "./App.vue";
import * as Sentry from "@sentry/vue";

import router from "./router";
import ElementPlus from "element-plus";
import i18n from "@/core/plugins/i18n";
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from "./stores/user";
import { storeResetPlugin } from "./stores/reset";

import axios from "axios"
import VueAxios from "vue-axios"

//imports for app initialization
import { initApexCharts } from "@/core/plugins/apexcharts";
import { initInlineSvg } from "@/core/plugins/inline-svg";
import { initVeeValidate } from "@/core/plugins/vee-validate";
import { initKtIcon } from "@/core/plugins/keenthemes";
import { installGlobalExceptionHandler } from "@/core/plugins/global-exception-handler";
import "@/core/plugins/prismjs";

import AuthService from "@/core/services/AuthService";
import AuditLogService from "@/core/services/AuditLogService";
import StoragePolicyService from "./core/services/StoragePolicyService";
import StorageBackendService from "./core/services/StorageBackendService";
import FileVersionService from "./core/services/FileVersionService";
import S3ApiService from "./core/services/S3ApiService";
import S3Gateway from "./core/services/S3Gateway";
import MigrationService from "./core/services/MigrationService";
import EdgeSupportService from "./core/services/EdgeSupportService";
import UsageService from "./core/services/UsageService";
import UserService from "./core/services/UserService";
import AdaptiveBucketPolicyService from "./core/services/AdaptiveBucketPolicyService";

const app = createApp(App);

Sentry.init({
  Vue: app,
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({router}),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [],
  // Session Replay
  replaysSessionSampleRate: 0.3, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  enabled: !!import.meta.env.VITE_APP_SENTRY_DSN
});

const pinia = createPinia();
pinia.use(storeResetPlugin);
app.use(pinia);
app.use(router);
app.use(ElementPlus);
installGlobalExceptionHandler(app);



// 401 interceptor
axios.interceptors.response.use(response => {
  // Any status code that lie within the range of 2xx cause this function to trigger
  return response;
}, error => {
  // Any status codes that falls outside the range of 2xx cause this function to trigger
  if (error.response?.status == 401 && !error.config?.skipAuthLogoutOn401){
    console.log("401 response received, logging out")
    // Remove the auth header, this token won't work any more
    delete axios.defaults.headers.common['Authorization']
    const authstore = useAuthStore();
    authstore.purgeAuth()
    // Remove cached user
    const userstore = useUserStore()
    userstore.delete_cached_user()
    // Redirect to login
    router.push({ name: 'sign-in' });
    // TODO show error?
  }

  return Promise.reject(error);
});

app.use(VueAxios, axios)
// Make the axios instance available via const axios = inject('axios') in components
app.provide('axios', app.config.globalProperties.axios)

// Pass the axios instance to all Services that require the SkyFlok Token
AuthService.init(app.axios)
AuditLogService.init(app.axios)
UserService.init(app.axios)
StoragePolicyService.init(app.axios)
StorageBackendService.init(app.axios)
S3ApiService.init(app.axios)
S3Gateway.init(app.axios)
MigrationService.init(app.axios)
FileVersionService.init(app.axios)
EdgeSupportService.init(app.axios)
UsageService.init(app.axios)
AdaptiveBucketPolicyService.init(app.axios)


initApexCharts(app);
initInlineSvg(app);
initKtIcon(app);
initVeeValidate();

app.use(i18n);

app.directive("tooltip", {
  // The tooltip must be destroyed before its host element is unloaded from the DOM, otherwise it hangs
  // If the host element is a button or link, its click also hides the tooltip
  mounted(el) {
    const typedEl = el as HTMLElement & { __tooltipHideOnClick?: () => void };
    Tooltip.getOrCreateInstance(typedEl);
    const tagName = typedEl.tagName?.toLowerCase();
    if (tagName === "a" || tagName === "button") {
      const hideOnClick = () => {
        const instance = Tooltip.getInstance(typedEl);
        if (!instance) {
          return;
        }
        instance.hide();
        instance.dispose();
      };
      typedEl.__tooltipHideOnClick = hideOnClick;
      typedEl.addEventListener("click", hideOnClick, { capture: true });
    }
  },
  beforeUnmount(el) {
    const typedEl = el as HTMLElement & { __tooltipHideOnClick?: () => void };
    const hideOnClick = typedEl.__tooltipHideOnClick;
    if (hideOnClick) {
      typedEl.removeEventListener("click", hideOnClick, { capture: true });
      delete typedEl.__tooltipHideOnClick;
    }

    const instance = Tooltip.getInstance(typedEl);
    if (!instance) {
      return;
    }
    instance.hide();
    instance.dispose();
  },
});

app.mount("#app");
